import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Chatbot from '../components/Chatbot';
import Footer from '../components/Footer';

export default function PrivacyPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar isPublic={true} />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12">
        <article className="prose prose-lg max-w-none">
          <h1 className="text-3xl font-bold text-gray-900 mb-8 mt-0">Privacy Policy</h1>
          
          <div className="text-sm text-gray-600 mb-8">
            Last Updated: January 13, 2025
          </div>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 leading-relaxed">
              GetFlowDocs ("FlowDocs", "we", "our", or "us") is committed to protecting your privacy. This Privacy Policy 
              explains how we collect, use, disclose, and safeguard your information when you use our service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">2.1 Information You Provide</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li><strong>Account Information:</strong> Name, email address, company name</li>
              <li><strong>Payment Information:</strong> Processed securely through Stripe (we do not store credit card details)</li>
              <li><strong>Salesforce Data:</strong> Flow metadata, object and field definitions, flow execution logs (when authorized)</li>
              <li><strong>Usage Data:</strong> Features used, flows analyzed, team collaboration activities</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">2.2 Automatically Collected Information</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li><strong>Log Data:</strong> IP address, browser type, operating system</li>
              <li><strong>Device Information:</strong> Device type, unique device identifiers</li>
              <li><strong>Analytics Data:</strong> Page views, feature usage, performance metrics</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">2.3 Cookies and Tracking</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Essential Cookies:</strong> For authentication and security</li>
              <li><strong>Analytics Cookies:</strong> To improve our service (can be disabled)</li>
              <li><strong>No Third-Party Advertising:</strong> We do not use advertising cookies</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">3.1 To Provide Services</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li>Generate AI-powered flow documentation</li>
              <li>Analyze flow performance and health</li>
              <li>Enable team collaboration features</li>
              <li>Process payments and manage subscriptions</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">3.2 To Improve Our Service</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li>Analyze usage patterns to enhance features</li>
              <li>Troubleshoot technical issues</li>
              <li>Develop new functionality based on user needs</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">3.3 To Communicate With You</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Service updates and feature announcements</li>
              <li>Security alerts and important notices</li>
              <li>Customer support responses</li>
              <li>Marketing communications (with your consent)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Data Sharing and Disclosure</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">4.1 We Do Not Sell Your Data</h3>
            <div className="p-4 bg-green-50 border-l-4 border-green-500 text-green-900 mb-4">
              <p className="font-semibold">We never sell, rent, or trade your personal information or Salesforce data.</p>
            </div>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">4.2 Service Providers</h3>
            <p className="text-gray-700 mb-3">We may share data with trusted service providers:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li><strong>Stripe:</strong> Payment processing</li>
              <li><strong>Supabase:</strong> Database and authentication</li>
              <li><strong>Anthropic:</strong> AI services (anonymized data only)</li>
              <li><strong>Vercel:</strong> Application hosting</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">4.3 Legal Requirements</h3>
            <p className="text-gray-700 mb-3">We may disclose information if required by:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li>Law or legal process</li>
              <li>To protect our rights or property</li>
              <li>To prevent fraud or security issues</li>
              <li>To protect the safety of any person</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">4.4 Business Transfers</h3>
            <p className="text-gray-700">
              In the event of a merger, acquisition, or sale of assets, your information may be transferred with advance notice.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data Security</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">5.1 Security Measures</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li>Encryption in transit (TLS 1.2+)</li>
              <li>Encryption at rest for sensitive data</li>
              <li>Regular security audits and testing</li>
              <li>Access controls and authentication</li>
              <li>Regular backups with encryption</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">5.2 Incident Response</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>72-hour notification for data breaches</li>
              <li>Detailed incident reports</li>
              <li>Remediation and prevention measures</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Your Rights and Choices</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">6.1 Access and Portability</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li>Request a copy of your data</li>
              <li>Export your data in common formats</li>
              <li>Access via self-service tools</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">6.2 Correction and Deletion</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li>Update your information at any time</li>
              <li>Request deletion of your account and data</li>
              <li>Some data may be retained for legal compliance</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">6.3 Marketing Communications</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li>Opt-out of marketing emails at any time</li>
              <li>Manage notification preferences in settings</li>
              <li>Transactional emails cannot be opted out</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">6.4 Cookie Preferences</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Disable non-essential cookies</li>
              <li>Browser settings to block cookies</li>
              <li>Does not affect service functionality</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. International Data Transfers</h2>
            <p className="text-gray-700">
              Your data may be transferred to and processed in countries other than your own. We ensure appropriate 
              safeguards are in place for such transfers.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Children's Privacy</h2>
            <p className="text-gray-700">
              FlowDocs is not intended for children under 18. We do not knowingly collect information from children.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. California Privacy Rights</h2>
            <p className="text-gray-700 mb-3">California residents have additional rights under CCPA:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Right to know what personal information is collected</li>
              <li>Right to know if personal information is sold or disclosed</li>
              <li>Right to opt-out of sale (we do not sell data)</li>
              <li>Right to equal service and price</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Oregon Privacy Rights</h2>
            <p className="text-gray-700 mb-3">Under Oregon law, residents have the right to:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Access their personal information</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of personal information</li>
              <li>Opt-out of the sale of personal information (we do not sell data)</li>
              <li>Not be discriminated against for exercising privacy rights</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Data Retention</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">11.1 Active Accounts</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li>Data retained while account is active</li>
              <li>Flow analysis history kept for trending</li>
              <li>Usage logs retained for 12 months</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">11.2 After Account Termination</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>30-day grace period for data recovery</li>
              <li>Anonymized aggregate data may be retained</li>
              <li>Legal compliance data as required</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. AI and Machine Learning</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">12.1 How We Use AI</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li>Generate flow summaries using Claude AI</li>
              <li>Analyze flow patterns for insights</li>
              <li>Improve documentation quality</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">12.2 AI Data Handling</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Flow data is processed but not stored by AI</li>
              <li>No training on customer data</li>
              <li>Anonymized patterns may improve service</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Third-Party Links</h2>
            <p className="text-gray-700">
              Our service may contain links to third-party sites. We are not responsible for their privacy practices.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Changes to This Policy</h2>
            <p className="text-gray-700">
              We may update this Privacy Policy periodically. We will notify you of material changes via email or through the service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">15. Contact Us</h2>
            <p className="text-gray-700 mb-3">For privacy-related questions:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Email: <a href="mailto:privacy@getflowdocs.com" className="text-blue-600 hover:underline">privacy@getflowdocs.com</a></li>
              <li>Data Protection Officer: <a href="mailto:dpo@getflowdocs.com" className="text-blue-600 hover:underline">dpo@getflowdocs.com</a></li>
              <li>Website: <a href="https://getflowdocs.com" className="text-blue-600 hover:underline">getflowdocs.com</a></li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">16. Specific Jurisdictions</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">16.1 European Union (GDPR)</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li>Legal basis: Contract performance and legitimate interests</li>
              <li>Data controller: GetFlowDocs</li>
              <li>Right to lodge complaint with supervisory authority</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">16.2 United Kingdom</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
              <li>UK GDPR compliance</li>
              <li>Similar rights as EU residents</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">16.3 Brazil (LGPD)</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>LGPD compliance measures in place</li>
              <li>Rights to access, correction, and deletion</li>
            </ul>
          </section>

        </article>
      </main>
      
      <Footer />
      <Chatbot />
    </div>
  );
}